/**
 * Irish Lights Aids-to-Navigation live feed (ra-gbrd.16).
 *
 * The committee imports official navigation aids by fetching the Commissioners
 * of Irish Lights (CIL) AtoN dataset LIVE at import time — never a bundled/stale
 * snapshot (per Chris's PR #706 review: "importing stale data from a static file").
 *
 * SOURCE
 * ------
 * The authoritative AtoN feed IL publish via data.gov.ie / the Marine Institute
 * ArcGIS Hub. It is a static-URL, CORS-enabled (Access-Control-Allow-Origin: *),
 * CC-BY-4.0 GeoJSON that IL refresh — so a browser fetch at import time gets the
 * current published positions with no proxy/token:
 *
 *   https://hub.arcgis.com/api/v3/datasets/144085aa4ec84da296f0c91ff967d96c_0
 *
 * (data.gov.ie's `irishlightsaton` resource 302-redirects here.) The FME Flow app
 * at fme.irishlights.ie is a session-gated Angular SPA whose datastreaming service
 * returns 401 to scripts, so it is NOT browser-reachable; this open Hub layer is
 * the live equivalent IL themselves publish.
 *
 * COVERAGE NOTE
 * -------------
 * This open layer is CIL AtoN class LIGHTHOUSE (the major lights, incl. Roches
 * Point at the Cork Harbour entrance). The numbered Cork fairway buoys
 * (No.3..No.18, Daunt) are NOT in any open IL dataset; they need a confirmed
 * AtoN position sheet from IL/Chris. Until then those buoys come via the
 * cross-club marks-library reuse path, not this feed. We import exactly what the
 * live feed publishes — we never invent a navigation position.
 *
 * SAFETY / DATA INTEGRITY
 * -----------------------
 * A wrong buoy coordinate is a navigational hazard, not a cosmetic bug. We only
 * ever surface a feature with a real, finite position from the live feed; any
 * feature we can't position is dropped rather than placed at a guessed location.
 */
import { slugify } from './slug';

/** Live, CORS-enabled, CC-BY-4.0 Irish Lights AtoN GeoJSON (see file header). */
export const IRISH_LIGHTS_ATON_URL =
  'https://hub.arcgis.com/api/v3/datasets/144085aa4ec84da296f0c91ff967d96c_0/downloads/data?format=geojson&spatialRefId=4326&where=1=1';

/** Coerce a value to a finite number, else undefined (strict — rejects junk). */
function finite(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value.trim());
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

/** True if lat/lng are finite AND within WGS84 bounds. A malformed upstream value
 *  (e.g. lat 999, or swapped lat/lng) must not reach a season as a real position
 *  — this is a navigation-aid path, so an out-of-range coordinate is rejected. */
function isValidLatLng(lat, lng) {
  return (
    lat !== undefined && lng !== undefined &&
    lat >= -90 && lat <= 90 &&
    lng >= -180 && lng <= 180
  );
}

/**
 * Map one GeoJSON feature to a candidate library mark, or null if it has no
 * usable position. Prefers the numeric `lat`/`long` properties; falls back to
 * the point geometry `[lng, lat]`.
 *
 * The returned mark carries our OWN typeable id (a slug of the AtoN name) — the
 * race officer keeps/edits it after import, exactly like a hand-entered mark —
 * plus library-only provenance (`source`, `sourceUrl`) that is stripped before
 * the mark is written to a season (see toStorableMark in marksLibrary).
 */
export function featureToMark(feature, fallbackIndex = 0) {
  const props = (feature && feature.properties) || {};
  const geom = (feature && feature.geometry) || {};
  const coords = Array.isArray(geom.coordinates) ? geom.coordinates : [];

  const latitude = finite(props.lat) ?? finite(props.PositionLatitudeDecimal) ?? finite(coords[1]);
  const longitude = finite(props.long) ?? finite(props.PositionLongitudeDecimal) ?? finite(coords[0]);
  if (!isValidLatLng(latitude, longitude)) return null;

  const name = String(props.AtonName || '').trim();
  if (!name) return null;

  const id = slugify(name) || `aton-${fallbackIndex}`;
  const mark = {
    id,
    name,
    type: 'racing_mark',
    latitude,
    longitude,
  };
  if (props.AtonTypeName) mark.appearance = String(props.AtonTypeName);
  // Library-only provenance (stripped before save).
  mark.source = String(props.LLAOrganisation || 'Commissioners of Irish Lights');
  if (props.Link) mark.sourceUrl = String(props.Link);
  return mark;
}

/**
 * Parse an Irish Lights AtoN GeoJSON FeatureCollection into candidate marks.
 * Pure (no network) so it is unit-testable against a fixture. Drops features
 * without a finite position and de-duplicates ids (appending -2, -3, ... to
 * collisions) so the picker never shows two marks with the same key.
 *
 * @param {object} geojson - a GeoJSON FeatureCollection
 * @returns {Array} candidate marks (canonical shape + provenance)
 */
export function parseIrishLightsGeoJSON(geojson) {
  const features = Array.isArray(geojson?.features) ? geojson.features : [];
  const seen = new Map(); // baseId -> count
  const marks = [];
  features.forEach((f, i) => {
    const mark = featureToMark(f, i);
    if (!mark) return;
    const base = mark.id;
    const n = (seen.get(base) || 0) + 1;
    seen.set(base, n);
    if (n > 1) mark.id = `${base}-${n}`;
    marks.push(mark);
  });
  marks.sort((a, b) => a.name.localeCompare(b.name));
  return marks;
}

/**
 * Fetch the live Irish Lights AtoN feed and return candidate marks.
 *
 * @param {{ signal?: AbortSignal, url?: string }} [opts]
 * @returns {Promise<Array>} candidate marks
 * @throws {Error} on a non-OK response or a body that isn't a FeatureCollection
 */
export async function fetchIrishLightsMarks({ signal, url = IRISH_LIGHTS_ATON_URL } = {}) {
  const res = await fetch(url, { signal });
  if (!res.ok) {
    throw new Error(`Irish Lights feed returned HTTP ${res.status}`);
  }
  const json = await res.json();
  if (!json || json.type !== 'FeatureCollection' || !Array.isArray(json.features)) {
    throw new Error('Irish Lights feed did not return a GeoJSON FeatureCollection');
  }
  return parseIrishLightsGeoJSON(json);
}
